import { z } from "zod";

// 11.6 D2 — freeze-picker config: recipe-declared select-implies-display. Styles/temps/mapping are DATA. State = option-set {selected} (reused, no new WidgetState variant). Renderer + Map entry land in Plan 05.
//
// Pure schema source — Plan 02 registers it into TEMPLATE_CATALOG (catalog union +
// entry) and into config-for-template (ConfigForTemplate/StateForTemplate branches);
// this file ships NO renderer and registers nothing. validateRecipe Pass 2A runs
// configSchema.safeParse(widget.config) so this schema is the config-shape gate a
// recipe's freeze-picker widget must cross.
//
// Shape rationale (closest analogs: option-set.ts for the bounded-option array +
// per-entry strictObject; static-note.ts for the simplest interface+strictObject
// isolatedDeclarations idiom). A freeze-picker is a single-select group over a
// recipe-declared set of styles; each style carries a label, an optional
// description, and a "select-implies-display" payload (a resolved primary display
// string plus an optional secondary line). The chosen style is render-side state
// reusing the EXISTING option-set { selected } shape — no new WidgetState variant,
// no 3-site lockstep change. The render/interaction model (roving radiogroup) lands
// in Plan 05.
//
// Generality (the C1 / META-07 grep gate): every field name and every comment here
// is generic — groupLabel / styles / id / label / desc / display / primary /
// secondary / defaultStyleId. The resolved-display payload uses the same
// domain-free primary/secondary positional roles scaling-matrix established for its
// multiplier bounds; NO recipe vocabulary appears as a field name, enum value, or
// literal (the engine never hardcodes a recipe's style names or its resolved
// display values). Recipe-declared style ids carry any domain meaning; the engine
// never names a recipe's vocabulary.
//
// DoS bounds (T-11.6-02-01 / T-11.6-02-03): styles is .min(1).max(12), every string
// carries a .max, and the whole config plus every nested object is z.strictObject
// (extra keys rejected). No z.any()/z.unknown(). An empty styles array is rejected —
// a picker with no styles is meaningless.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes (D-15):
// every exported type is an explicit interface and the exported const carries a
// `z.ZodType<FreezePickerConfig>` annotation so tsc emits declarations without
// reading across module boundaries (no TS9013). The per-style sub-shape is its own
// exported interface + private `_freezeStyleSchema` const (the option-set
// `_optionEntrySchema` sub-shape precedent) so isolatedDeclarations stays clean.

export interface FreezeStyleDisplay {
  // The resolved "select-implies-display" payload. `primary` is the headline
  // resolved value the picker drives (recipe DATA — e.g. a resolved heat/measure
  // string); `secondary` is an optional follow-on line (recipe DATA — e.g. a note).
  // Both are domain-free positional roles, never recipe vocabulary.
  primary: string;
  secondary?: string | undefined;
}

export interface FreezeStyle {
  id: string;
  label: string;
  desc?: string | undefined;
  display: FreezeStyleDisplay;
}

export interface FreezePickerConfig {
  // The picker-group heading (recipe DATA — e.g. the candy-freeze block label).
  groupLabel: string;
  styles: ReadonlyArray<FreezeStyle>;
  // When present, the recipe's default-selected style id (the reference defaults to
  // its first style). A non-empty string; cross-reference to a styles[].id is
  // recipe-root walk territory, not enforced at this template-level schema.
  defaultStyleId?: string | undefined;
}

const _freezeStyleDisplaySchema = z.strictObject({
  primary: z.string().min(1).max(120),
  secondary: z.string().min(1).max(300).optional(),
});

const _freezeStyleSchema = z.strictObject({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  desc: z.string().min(1).max(300).optional(),
  display: _freezeStyleDisplaySchema,
});

export const FreezePickerConfigSchema: z.ZodType<FreezePickerConfig> = z.strictObject({
  groupLabel: z.string().min(1).max(100),
  styles: z.array(_freezeStyleSchema).min(1).max(12),
  defaultStyleId: z.string().min(1).max(64).optional(),
});
